/* ============================================
   TFS Wheels — PDP Cross-Sell Entry Point
   Shared by main-product-wt.liquid and
   main-product-merged.liquid (formerly two
   byte-identical inline <script> blocks — diffed
   clean at extraction time, 2026-07-07). Drives the
   "Add Tires" / "Add Wheels" CTA rendered by the
   cross_sell_cta block on every wheel/tire PDP.

   Dual-protocol (Package Builder Stage 3 Task 2):
   this asset ADDS the new live-selection package
   handoff (pkgW/pkgT slot-grammar params → the grid
   → the builder) while reproducing the legacy paths
   verbatim:
     - checkCartAndUpdate checkout-mode morph
     - restoreDefault + bfcache pageshow restore
     - the legacy no-deal path (add now, browse after)
     - the legacy deferred-wheel package stash
       (tfs_pkg_wheel, read by pdp-package-price.liquid
       on the tire PDP) — still written on the wheels
       path for one release; new-mode surfaces never
       read it back.

   Button contract (do not rename — pdp-group-picker.js
   proxies clicks onto .tfs-cross-sell and mirrors its
   disabled/data-mode state; pdp-group-picker.css hides
   the raw button while the picker card is live):
     .tfs-cross-sell.pdp-cross-sell-cta
     data-target        "tires" | "wheels"
     data-diameter       landing-product fallback only
     data-page-type      "wheels" | "tires"
     data-default-text   label restored by restoreDefault
     data-pkg-wheel       (wheels page only) legacy stash gate
     data-pkg-title
     data-mode            set to "checkout" by checkCartAndUpdate

   Live-selection contract (pdp-group-picker.js, Stage 3
   Task 1): window.__TFS_GP_SELECTION__ = {
     productId, handle, at,
     front: {pid, vid, qty, dia, width},
     rear:  {pid, vid, qty, dia, width} | null,
     type:  'wheels' | 'tires'
   }, plus the tfs:group-selection CustomEvent (detail
   .available) used here to gate the button on the
   SELECTED member rather than the landing variant.

   Slot-grammar emission: [tag:]pid[:vid[:qty]] members
   joined by comma, front tag omitted (defaults to f),
   rear always tagged r: — mirrors
   extensions/fitment-extension/assets/slot-parser.js
   serializeSlots (grammar spec §5.1,
   docs/specs/2026-07-06-package-builder-design.md).
   The full parser isn't needed on the PDP (emission
   only, never parsed here) so this is a tiny local
   emitter rather than loading slot-parser.js on every
   PDP.
   ============================================ */
(function () {
  'use strict';

  /* ---- helpers (legacy, verbatim) ---- */
  function getSelectedVariantId() {
    var input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (input) return input.value;
    var select = document.querySelector('form[action*="/cart/add"] select[name="id"]');
    if (select) return select.value;
    return null;
  }

  function getSelectedQuantity() {
    var input = document.querySelector('form[action*="/cart/add"] input[name="quantity"]');
    return input ? parseInt(input.value, 10) || 1 : 1;
  }

  /* Diameter with specs-table fallback */
  function getDiameter(btn) {
    var dia = btn.getAttribute('data-diameter');
    if (dia && dia !== '') return dia;
    var rows = document.querySelectorAll('.specs-table tr');
    for (var i = 0; i < rows.length; i++) {
      var label = rows[i].querySelector('td:first-child');
      if (label && label.textContent.trim() === 'Diameter') {
        var val = rows[i].querySelector('td:last-child');
        if (val) return val.textContent.replace(/[^0-9.]/g, '');
      }
    }
    return dia;
  }

  /* Build collection URL — diameter only, no vehicle params.
     Vehicle is already in the garage; passing it causes
     Convermax to auto-set OEM diameter instead of ours.
     pkgWheelId arms the package-deal savings badges on the
     tire grid (product-grid.js package mode) — legacy dual-write. */
  function buildRedirectUrl(target, diameter, pkgWheelId) {
    var collection = target === 'tires' ? 'tires' : 'wheels';
    var filterKey = target === 'tires' ? 'tireDiameter' : 'diameter';
    var url = '/collections/' + collection + '?' + filterKey + '=' + encodeURIComponent(diameter);
    if (pkgWheelId && target === 'tires') {
      url += '&pkgWheel=' + encodeURIComponent(pkgWheelId);
    }
    /* Flagged Phase 4 rollout: keep the package-review-page
       funnel alive from the wheel PDP into the grid. */
    try {
      if (new URLSearchParams(window.location.search).get('pkgReview') === '1') {
        url += '&pkgReview=1';
      }
    } catch (e) { /* ignore */ }
    return url;
  }

  /* Stash wheel context for the grid banner AND the deferred
     legacy package add: the wheel is NOT added here — the tire
     PDP's "Add Package to Cart" adds wheels + tires together.
     Carries THIS page's fitment result (fit) so the tire PDP
     can replay it during the wheel's add — that's what drives
     cart-interceptor's lug-kit/hub-ring auto-add (thread size,
     hub bores) and the wheel line's Vehicle/Verdict notes.
     Dual-write: still written on every wheels-page click for one
     release (the legacy tire-PDP overlay's combined-add reads
     it via pdp-package-price.liquid resolveContext) even though
     the NEW package path (buildPackageUrl) never reads it back —
     vid/qty here prefer the live picker SELECTION over the form
     when available, so the stash matches whatever the shopper is
     actually about to browse away with. */
  function stashPackageContext(btn, sel) {
    var pid = btn.getAttribute('data-pkg-wheel');
    if (!pid) return null;
    var fitSnapshot = null;
    try {
      if (window.__TFS_FITMENT_RESULT__) {
        fitSnapshot = JSON.parse(JSON.stringify(window.__TFS_FITMENT_RESULT__));
      }
    } catch (e) { fitSnapshot = null; }
    var vid = (sel && sel.front && sel.front.vid != null) ? sel.front.vid : getSelectedVariantId();
    var qty = (sel && sel.front && sel.front.qty != null) ? sel.front.qty : getSelectedQuantity();
    /* data-pkg-savings/data-pkg-until are gone from the Liquid markup
       (Stage 3 Task 2 — grep-verified dead: the only reader was this same
       inline script, and pdp-package-price.liquid's resolveContext/
       applyDisplay never read stash.ss/stash.title back — the tire PDP
       always re-fetches the discount fresh via /package-badge). ss/title
       keys are kept in the stash shape for back-compat with any code that
       might inspect it, just always empty now. until now always reads 0,
       which resolveContext's freshness gate (!stash.until || ...) treats
       as "no expiry" — fails OPEN, not closed; safe since deal freshness
       is no longer this button's concern. */
    try {
      sessionStorage.setItem('tfs_pkg_wheel', JSON.stringify({
        pid: pid,
        title: btn.getAttribute('data-pkg-title') || '',
        ss: '',
        until: 0,
        vid: vid,
        qty: qty,
        fit: fitSnapshot
      }));
    } catch (e) { /* private mode — param alone still works */ }
    return pid;
  }

  /* ---- cart-aware: swap to single "Add to Cart & View Cart" CTA (legacy, verbatim) ---- */
  function checkCartAndUpdate(btn) {
    /* Package-flow entry (owner spec 2026-08-18): NEVER checkout-morph while
       ?fromPackages=true — the morph hid the .pdp-actions row ("Buy Wheels
       Only" vanished) and turned "Add Tires →" into a native wheel-add +
       /cart redirect whenever the cart already held a tire, silently
       killing the flow. The flow-end scrub (cart-interceptor) removes
       fromPackages after a Buy Wheels Only add, so post-flow page loads get
       the legacy morph back. */
    try {
      if (new URLSearchParams(window.location.search).get('fromPackages') === 'true') return;
    } catch (e) { /* ignore */ }
    var pageType = btn.getAttribute('data-page-type');
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var hasWheels = false;
        var hasTires = false;
        (cart.items || []).forEach(function (item) {
          var t = (item.product_type || '').toLowerCase();
          if (t.indexOf('wheel') !== -1) hasWheels = true;
          if (t.indexOf('tire') !== -1) hasTires = true;
        });

        var complementInCart =
          (pageType === 'wheels' && hasTires) ||
          (pageType === 'tires' && hasWheels);

        if (complementInCart) {
          /* Hide the normal Add to Cart + cross-sell, show the
             package-complete CTA that routes to the cart. */
          btn.setAttribute('data-mode', 'checkout');
          btn.textContent = 'Add to Cart & View Cart →';
          btn.classList.add('pdp-cross-sell-cta--checkout');
          var addBtn = document.querySelector('.pdp-add-cart');
          if (addBtn) addBtn.closest('.pdp-actions').style.display = 'none';
        }
      })
      .catch(function () { /* silent — keep default CTA */ });
  }

  function restoreDefault(btn) {
    btn.disabled = false;
    btn.textContent = btn.getAttribute('data-default-text') || 'Add to Order →';
    btn.removeAttribute('data-mode');
    btn.classList.remove('pdp-cross-sell-cta--checkout');
    var addBtn = document.querySelector('.pdp-add-cart');
    if (addBtn) addBtn.closest('.pdp-actions').style.display = '';
  }

  /* ---- slot-grammar emission (tiny local mirror — emission only) ----
     One member -> "[tag:]pid[:vid[:qty]]". Grammar is POSITIONAL, not a
     bag of optional segments: pid[:vid[:qty]] — qty can only appear when
     vid is also present, because the parser reads segs[1] as vid and
     segs[2] as qty by POSITION, not by looking ahead for which is which
     (parseSlotParam). A naive filter-out-nullish-then-join (mirroring
     serializeSlots' shape but not its calling discipline) corrupts the
     wire string whenever vid is null but qty is set — e.g.
     [pid, null, 4].filter(...).join(':') => "pid:4", which the parser
     reads as pid:vid="4" (PID_RE on "4" fails => the WHOLE param
     rejects; a >=5-digit qty would instead falsely parse AS a vid).
     Fix: build the token by explicit precedence — always pid; append vid
     only if present; append qty only if BOTH vid and qty are present
     (a qty with no vid is simply dropped — pid-only tokens are valid
     grammar and qty defaults server-side, so nothing is lost). Front's
     tag is omitted (grammar default 'f' for the first member); rear is
     always explicitly tagged 'r' since it's never guaranteed to be the
     second member when only-rear edge cases are formed upstream
     (defensive; the picker always fills front first in practice). */
  function emitSlotMember(tag, pid, vid, qty) {
    var segs = [];
    if (tag !== null && tag !== undefined && tag !== '') segs.push(tag);
    segs.push(pid);
    if (vid !== null && vid !== undefined && vid !== '') {
      segs.push(vid);
      if (qty !== null && qty !== undefined && qty !== '') segs.push(qty);
    }
    return segs.join(':');
  }

  function emitSlots(front, rear) {
    var members = [];
    if (front && front.pid) members.push(emitSlotMember(null, front.pid, front.vid, front.qty));
    if (rear && rear.pid) members.push(emitSlotMember('r', rear.pid, rear.vid, rear.qty));
    return members.join(',');
  }

  /* ---- live-selection read (S2-T4-style fallback chain) ----
     Prefers the picker's __TFS_GP_SELECTION__ (fresh — written on
     every applySelection/engageRear/disengageRear, including the
     initial render); falls back to the classic form/button reads
     when the picker never ran (API failure, non-group pages) or the
     global is stale/foreign (different landing product). */
  function readSelection(btn) {
    var sel = null;
    try { sel = window.__TFS_GP_SELECTION__; } catch (e) { sel = null; }
    if (sel && sel.front && sel.front.pid) return sel;

    var fallbackDia = getDiameter(btn);
    return {
      front: {
        pid: null,
        vid: getSelectedVariantId(),
        qty: getSelectedQuantity(),
        dia: fallbackDia,
        width: null
      },
      rear: null,
      type: btn.getAttribute('data-page-type') === 'tires' ? 'tires' : 'wheels'
    };
  }

  /* NEW package path (the flip): reads the live selection at click
     time and builds the slot-grammar handoff into the grid. Fires
     whenever the landing page has a complement (wheels<->tires) —
     the xs_pkg_live/deal-gated path is gone; deal-ness resolves
     downstream (server-side /group pkg annotations + the checkout
     Function), not from this button. */
  function buildPackageUrl(pageType, sel, diameter) {
    var front = sel.front || {};
    var rear = sel.rear || null;
    var slots = emitSlots(front, rear);

    /* Owner spec 2026-08-18: the handoff carries the SELECTED product's
       diameter as the destination grid's filter, plus the TIRE leg's
       fitment scope fit=g|all. Per-leg scopes (owner 2026-08-26): fit is
       the tire leg's OWN aggressive control and is never derived from the
       wheel leg's includeMods anymore — turning mod-required wheels off
       must not strip aggressive tire sizes from the tire step (a stock-fit
       wheel with an aggressive tire is a normal build). The scope rides
       the flow as-is: preserve an explicit fit already on this URL (the
       tire leg's earlier choice, forwarded by the PDP context params),
       otherwise default to 'all' — the storefront's aggressive-fits-ON
       default. The legacy pkgWheel dual-write is retired — nothing
       downstream reads it in the redesigned flow, and it armed the deal
       badges the tire step no longer shows. */
    var fitScope = 'all';
    try {
      var pageFit = new URLSearchParams(window.location.search).get('fit');
      if (pageFit === 'g' || pageFit === 'all') fitScope = pageFit;
    } catch (e) { /* ignore */ }

    if (pageType === 'wheels') {
      /* Picker-failure fallback (readSelection's non-picker branch):
         front.pid is null, so slots is '' and pkgW= is omitted below —
         the grid gets a clean URL (tireDiameter alone). */
      var url = '/collections/tires?tireDiameter=' + encodeURIComponent(diameter)
        + '&fit=' + fitScope;
      if (slots) url += '&pkgW=' + encodeURIComponent(slots);
      try {
        var wp = new URLSearchParams(window.location.search);
        /* A wheel PDP reached mid-flow can carry an existing tire draft
           (builder swap-wheels hop) — forward it so swapping one side never
           discards the other. */
        var keptT = wp.get('pkgT');
        if (keptT) url += '&pkgT=' + encodeURIComponent(keptT);
        if (wp.get('pkgReview') === '1') url += '&pkgReview=1';
      } catch (e) { /* ignore */ }
      return url;
    }

    /* tires page (tires-first): same shape toward the wheels grid.
       Omit pkgT= entirely when slots is empty (picker-failure fallback,
       front.pid null) — an empty pkgT param is harmless noise the
       parser just rejects, but omitting it keeps the URL clean when
       there's genuinely nothing to hand off. */
    var wheelsUrl = '/collections/wheels?diameter=' + encodeURIComponent(diameter)
      + '&fit=' + fitScope;
    if (slots) wheelsUrl += '&pkgT=' + encodeURIComponent(slots);
    try {
      /* Symmetric other-side forward (see the wheels branch above). */
      var keptW = new URLSearchParams(window.location.search).get('pkgW');
      if (keptW) wheelsUrl += '&pkgW=' + encodeURIComponent(keptW);
    } catch (e) { /* ignore */ }
    return wheelsUrl;
  }

  /* ---- click handler ---- */
  function handleClick(e) {
    var btn = e.currentTarget;
    var mode = btn.getAttribute('data-mode');

    /* Package complete: add the item, then go to the CART
       (not checkout — auto-jumping to checkout inflates ad
       conversion data and skips cart review). */
    if (mode === 'checkout') {
      var vid = getSelectedVariantId();
      if (!vid) { window.location.href = '/cart'; return; }
      var qty = getSelectedQuantity();
      btn.disabled = true;
      btn.textContent = 'Adding to cart…';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(vid, 10), quantity: qty })
      }).then(function () {
        window.location.href = '/cart';
      }).catch(function () {
        window.location.href = '/cart';
      });
      return;
    }

    var target = btn.getAttribute('data-target');
    var pageType = btn.getAttribute('data-page-type');

    /* Buttons without a complement target (data-target unset — e.g. an
       accessories page type, matching what the old script implicitly
       gated on) fall through to nothing, same as before. */
    if (!target) return;

    var sel = readSelection(btn);
    var diameter = (sel.front && sel.front.dia) || getDiameter(btn);
    if (!diameter) return;

    /* NEW package path fires whenever this page type has a complement
       target — regardless of the landing product's deal metafield. */
    if (pageType === 'wheels' || pageType === 'tires') {
      /* A deliberate package start beats any earlier cancel: drop the
         cancelled-draft tombstone (written by the grid/builder ✕ — see
         tfsPkgCancelMatch in product-grid.js) so restarting the SAME
         wheel's package right after cancelling it works. */
      try { sessionStorage.removeItem('tfs_pkg_cancelled'); } catch (e) { /* ignore */ }
      /* 2026-08-18: the legacy tfs_pkg_wheel stash write is retired with
         the combined-add funnel — the draft lives entirely in the URL
         (pkgW slot grammar); the tire PDP routes to the builder. */
      try { sessionStorage.setItem('tfs_cross_sell_diameter', diameter); } catch (e) { /* ignore */ }
      window.location.href = buildPackageUrl(pageType, sel, diameter);
      return;
    }

    /* Legacy cross-sell (no complement flow recognized): add this item
       now, then browse. Unreachable today (every rendered cross-sell
       button is page-type wheels|tires) — kept as the historical
       fallback the old script also had via its no-pkgWheelId branch. */
    var variantId = getSelectedVariantId();
    if (!variantId) {
      alert('Please select a variant first.');
      return;
    }
    var quantity = getSelectedQuantity();
    var originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding to cart…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: quantity })
    })
      .then(function (resp) {
        if (!resp.ok) throw new Error('Add to cart failed');
        try { sessionStorage.setItem('tfs_cross_sell_diameter', diameter); } catch (e) { /* ignore */ }
        window.location.href = buildRedirectUrl(target, diameter, null);
      })
      .catch(function (err) {
        console.error('TFS cross-sell error:', err);
        btn.disabled = false;
        btn.textContent = originalText;
        alert('Failed to add to cart. Please try again.');
      });
  }

  /* ---- availability gate (Stage 3 Task 2 #4): key the CTA on the
     SELECTED member, not the landing variant. Liquid renders the
     landing state (current_variant.available) as the before-JS
     default via the disabled attribute; this listener keeps it in
     sync as the picker's selection changes. ---- */
  function applyAvailability(btn, available, reason) {
    if (btn.getAttribute('data-mode') === 'checkout') return; /* checkout CTA owns its own state */
    if (available === false) {
      btn.disabled = true;
      btn.title = reason || 'Sold out in this size';
    } else if (available === true) {
      btn.disabled = false;
      btn.removeAttribute('title');
    }
  }

  function onGroupSelection(e) {
    var detail = e && e.detail;
    if (!detail) return;
    document.querySelectorAll('.tfs-cross-sell').forEach(function (btn) {
      applyAvailability(btn, detail.available, 'Sold out in this size');
    });
  }

  /* ---- init ---- */
  document.querySelectorAll('.tfs-cross-sell').forEach(function (btn) {
    btn.addEventListener('click', handleClick);
    checkCartAndUpdate(btn);
  });

  document.addEventListener('tfs:group-selection', onGroupSelection);

  /* Fix frozen button on back-navigation (bfcache) */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      document.querySelectorAll('.tfs-cross-sell').forEach(function (btn) {
        restoreDefault(btn);
        checkCartAndUpdate(btn);
      });
    }
  });
})();
